/**
 * One report per tracked contact (watchlist, drone, co-traveler).
 *
 * The monitor already keys these on `threat_key`. This file turns the event
 * stream — plus the co-traveler row when we have it — into the facts an
 * operator reads against the route: first seen, how it has moved with us,
 * where it is relative to the line, and how much we should trust it.
 */
import {
  MissionCotraveler,
  MissionRouteEvent,
  MissionRouteEventType,
  MissionTimelineStop,
} from '../../services/missionApi';
import { formatMeters, formatSpan } from './format';

export type TrackedKind = 'watchlist' | 'drone' | 'cotraveler';

export interface ThreatLocation {
  latitude: number;
  longitude: number;
  mgrs?: string;
  decimal: string;
  offsetM?: number | null;
  alongM?: number | null;
  bearingDeg?: number | null;
  leg?: number | null;
  accuracyM?: number | null;
  waypoint?: string | null;
}

export interface ThreatReport {
  id: string;
  kind: TrackedKind;
  title: string;
  identifier: string;
  trust: { label: string; tone: 'crit' | 'warn' | 'ok'; detail: string };
  firstObserved: string | null;
  firstObservedDtg: string;
  sightings: number;
  places: number;
  cotravel: string | null;
  lastDwell: string | null;
  location: ThreatLocation | null;
  source: { grade: string; label: string };
  confidence: { label: string; detail: string };
  watchlistId?: number | null;
  canRouteAround: boolean;
  canMarkOurs: boolean;
  excludeIdentifier?: string | null;
  excludeSignalType?: string | null;
  excludeName?: string | null;
  excludeManufacturerId?: number | null;
}

const TRACKED: Record<string, TrackedKind> = {
  watchlist_on_route: 'watchlist',
  uas_intercept: 'drone',
  cotraveler_suspected: 'cotraveler',
  cotraveler_confirmed: 'cotraveler',
  cotraveler_sustained: 'cotraveler',
};

const MONTHS = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN', 'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'];

export function trackedKind(
  eventType?: MissionRouteEventType | string | null,
  threatKey?: string | null,
): TrackedKind | null {
  if (eventType && TRACKED[eventType]) return TRACKED[eventType];
  const key = String(threatKey || '').toLowerCase();
  if (key.startsWith('watchlist:') || key.startsWith('wl:') || key.startsWith('proximity:')) return 'watchlist';
  if (key.startsWith('uas:')) return 'drone';
  if (key.startsWith('cotraveler:')) return 'cotraveler';
  return null;
}

export function formatDtg(iso?: string | null): string {
  if (!iso) return '—';
  const when = new Date(iso);
  if (Number.isNaN(when.getTime())) return '—';
  const dd = String(when.getUTCDate()).padStart(2, '0');
  const hh = String(when.getUTCHours()).padStart(2, '0');
  const mm = String(when.getUTCMinutes()).padStart(2, '0');
  const mon = MONTHS[when.getUTCMonth()];
  const yy = String(when.getUTCFullYear()).slice(-2);
  return `${dd}${hh}${mm}Z${mon}${yy}`;
}

export function formatDecimal(lat: number, lon: number): string {
  return `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
}

export function formatMgrs(lat: number, lon: number): string | undefined {
  try {
    // eslint-disable-next-line @typescript-eslint/no-var-requires
    const mgrs = require('mgrs') as { forward: (ll: [number, number], acc?: number) => string };
    const raw = mgrs.forward([lon, lat], 5);
    if (!raw) return undefined;
    return raw.replace(/^(\d{1,2}[A-Z])([A-Z]{2})(\d+)$/i, (_, zone, square, digits) => {
      const half = Math.floor(digits.length / 2);
      return `${zone} ${square} ${digits.slice(0, half)} ${digits.slice(half)}`;
    });
  } catch {
    return undefined;
  }
}

function num(value: unknown): number | null {
  const n = Number(value);
  return Number.isFinite(n) ? n : null;
}

function str(value: unknown): string | null {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed || null;
}

function bearingDeg(
  from: { latitude: number; longitude: number },
  to: { latitude: number; longitude: number },
): number | null {
  const φ1 = (from.latitude * Math.PI) / 180;
  const φ2 = (to.latitude * Math.PI) / 180;
  const Δλ = ((to.longitude - from.longitude) * Math.PI) / 180;
  const y = Math.sin(Δλ) * Math.cos(φ2);
  const x = Math.cos(φ1) * Math.sin(φ2) - Math.sin(φ1) * Math.cos(φ2) * Math.cos(Δλ);
  if (Math.abs(y) < 1e-9 && Math.abs(x) < 1e-9) return null;
  return (Math.atan2(y, x) * 180 / Math.PI + 360) % 360;
}

function alongOf(event: MissionRouteEvent): number | null {
  return num(event.meta?.distance_along_m);
}

function nearestStop(
  alongM: number | null,
  timeline?: MissionTimelineStop[] | null,
): MissionTimelineStop | null {
  if (alongM == null || !timeline?.length) return null;
  return [...timeline].sort((a, b) => (
    Math.abs((a.distance_along_m || 0) - alongM) - Math.abs((b.distance_along_m || 0) - alongM)
  ))[0] || null;
}

function legOf(
  event: MissionRouteEvent | undefined,
  alongM: number | null,
  timeline?: MissionTimelineStop[] | null,
): number | null {
  if (timeline && timeline.length >= 2 && alongM != null) {
    let leg = 1;
    for (let i = 1; i < timeline.length; i += 1) {
      const start = timeline[i - 1].distance_along_m ?? 0;
      if (alongM >= start) leg = i;
    }
    return leg;
  }
  if (event?.segment_index != null && Number.isFinite(event.segment_index)) {
    return Math.max(1, event.segment_index + 1);
  }
  return null;
}

function uniqueId(events: MissionRouteEvent[], cotraveler?: MissionCotraveler | null): string {
  const key = cotraveler?.threat_key
    || events.map((e) => e.threat_key).find(Boolean)
    || '';
  if (key) return key;
  const ident = str(events[0]?.meta?.identifier)
    || str(events[0]?.meta?.drone_mac)
    || str(events[0]?.meta?.uas_id)
    || events[0]?.label
    || `event:${events[0]?.id || 'unknown'}`;
  return ident;
}

function identifierOf(events: MissionRouteEvent[], cotraveler?: MissionCotraveler | null): string {
  if (cotraveler?.identifier) return cotraveler.identifier;
  const meta = events[0]?.meta || {};
  return str(meta.identifier)
    || str(meta.external_id)
    || str(meta.drone_mac)
    || str(meta.uas_id)
    || str(meta.watchlist_label)
    || events[0]?.label
    || uniqueId(events, cotraveler);
}

function titleOf(
  kind: TrackedKind,
  events: MissionRouteEvent[],
  cotraveler?: MissionCotraveler | null,
): string {
  if (cotraveler?.watchlist_label) return cotraveler.watchlist_label;
  if (cotraveler?.name) return cotraveler.name;
  const latest = events[0];
  const meta = latest?.meta || {};
  return str(meta.watchlist_label)
    || str(meta.name)
    || str(meta.ssid)
    || latest?.label
    || identifierOf(events, cotraveler)
    || (kind === 'drone' ? 'Drone' : kind === 'watchlist' ? 'Watchlist' : 'Co-traveler');
}

function trustOf(
  kind: TrackedKind,
  events: MissionRouteEvent[],
  cotraveler?: MissionCotraveler | null,
): ThreatReport['trust'] {
  const track = str(cotraveler?.track) || str(events[0]?.meta?.track) || '';
  const watchlisted = !!(cotraveler?.watchlist_id || events.some((e) => e.event_type === 'watchlist_on_route'));
  if (track === 'identity' || track === 'identity+inference') {
    return {
      label: watchlisted ? 'Watchlisted · identity' : 'Identified',
      tone: 'warn',
      detail: 'Known emitter, matched by identifier',
    };
  }
  if (kind === 'drone') {
    return { label: 'Untrusted · air contact', tone: 'crit', detail: 'Outside TOFU baseline' };
  }
  if (watchlisted) {
    return { label: 'Watchlisted · untrusted', tone: 'crit', detail: 'On the list, not own kit' };
  }
  return { label: 'Untrusted · outside TOFU baseline', tone: 'crit', detail: 'Not in the mission RF baseline' };
}

function sourceOf(
  kind: TrackedKind,
  events: MissionRouteEvent[],
  cotraveler?: MissionCotraveler | null,
): ThreatReport['source'] {
  const track = str(cotraveler?.track) || str(events[0]?.meta?.track) || '';
  const source = str(events[0]?.meta?.source);
  if (kind === 'drone' && source === 'remote_id') {
    return { grade: 'B2', label: 'Usually reliable / Remote ID' };
  }
  if (track === 'identity' || track === 'identity+inference') {
    return { grade: 'B2', label: 'Usually reliable / identifier match' };
  }
  if (kind === 'cotraveler') {
    return { grade: 'C3', label: 'Fairly reliable / inferred companion' };
  }
  return { grade: 'C3', label: 'Fairly reliable / single sensor' };
}

function confidenceOf(
  events: MissionRouteEvent[],
  cotraveler?: MissionCotraveler | null,
): ThreatReport['confidence'] {
  const score = num(cotraveler?.score) ?? num(events[0]?.meta?.score) ?? num(events[0]?.meta?.confidence);
  const sensors = new Set(
    events.flatMap((e) => [str(e.meta?.signal_type), str(e.meta?.source)].filter(Boolean) as string[]),
  );
  if (score != null && score >= 0.8) {
    return { label: 'High', detail: sensors.size > 1 ? 'Multi-sensor, high score' : 'High score on one sensor' };
  }
  if (score != null && score >= 0.5) {
    return { label: 'Moderate', detail: sensors.size > 1 ? 'Fused sensors' : 'Single-sensor, no visual' };
  }
  return { label: 'Low to moderate', detail: 'Single-sensor, no visual' };
}

function lastDwell(
  events: MissionRouteEvent[],
  cotraveler: MissionCotraveler | null | undefined,
  location: ThreatLocation | null,
  units: 'km' | 'mi',
): string | null {
  const parts: string[] = [];
  if (cotraveler?.shared_seconds) parts.push(formatSpan(cotraveler.shared_seconds));
  else if (events.length >= 2) {
    const newest = new Date(events[0].detected_at).getTime();
    const oldest = new Date(events[events.length - 1].detected_at).getTime();
    if (Number.isFinite(newest) && Number.isFinite(oldest) && newest > oldest) {
      parts.push(formatSpan((newest - oldest) / 1000));
    }
  }
  if (location?.waypoint) parts.push(`${location.waypoint} vicinity`);
  else if (location?.offsetM != null) {
    const off = formatMeters(location.offsetM, units);
    if (off) parts.push(`${off} off route`);
  }
  return parts.length ? parts.join(' · ') : null;
}

function locationOf(
  events: MissionRouteEvent[],
  cotraveler: MissionCotraveler | null | undefined,
  timeline: MissionTimelineStop[] | undefined,
): ThreatLocation | null {
  const located = events.find((e) => Number.isFinite(e.latitude) && Number.isFinite(e.longitude))
    || (cotraveler && Number.isFinite(cotraveler.latitude) && Number.isFinite(cotraveler.longitude)
      ? { latitude: cotraveler.latitude, longitude: cotraveler.longitude, meta: {}, segment_index: cotraveler.segment_index } as MissionRouteEvent
      : null);
  if (!located || located.latitude == null || located.longitude == null) return null;
  const lat = Number(located.latitude);
  const lon = Number(located.longitude);
  const withFix = events.filter((e) => Number.isFinite(e.latitude) && Number.isFinite(e.longitude));
  const previous = withFix[1];
  const alongM = alongOf(located as MissionRouteEvent);
  const stop = nearestStop(alongM, timeline);
  return {
    latitude: lat,
    longitude: lon,
    mgrs: formatMgrs(lat, lon),
    decimal: formatDecimal(lat, lon),
    offsetM: num((located as MissionRouteEvent).distance_m) ?? num(located.meta?.offset_m),
    alongM,
    bearingDeg: previous
      ? bearingDeg(
        { latitude: Number(previous.latitude), longitude: Number(previous.longitude) },
        { latitude: lat, longitude: lon },
      )
      : num(located.meta?.heading_deg),
    leg: legOf(located as MissionRouteEvent, alongM, timeline),
    accuracyM: num(located.meta?.position_sigma_m) ?? num(located.meta?.accuracy_m),
    waypoint: stop?.label || null,
  };
}

export function eventsForThreat(
  events: MissionRouteEvent[],
  threatKey?: string | null,
): MissionRouteEvent[] {
  if (!threatKey) return [];
  return events
    .filter((event) => event.threat_key === threatKey)
    .sort((a, b) => new Date(b.detected_at).getTime() - new Date(a.detected_at).getTime());
}

export function buildThreatReport(args: {
  events: MissionRouteEvent[];
  cotraveler?: MissionCotraveler | null;
  threatKey?: string | null;
  timeline?: MissionTimelineStop[] | null;
  units?: 'km' | 'mi';
}): ThreatReport | null {
  const units = args.units || 'mi';
  const key = args.threatKey || args.cotraveler?.threat_key || args.events[0]?.threat_key || null;
  const scoped = key
    ? (args.events.some((e) => e.threat_key === key)
      ? eventsForThreat(args.events, key)
      : [...args.events].sort((a, b) => new Date(b.detected_at).getTime() - new Date(a.detected_at).getTime()))
    : [...args.events].sort((a, b) => new Date(b.detected_at).getTime() - new Date(a.detected_at).getTime());
  if (!scoped.length && !args.cotraveler) return null;

  const kind = trackedKind(scoped[0]?.event_type, key || args.cotraveler?.threat_key);
  if (!kind) return null;

  const first = scoped[scoped.length - 1]?.detected_at || args.cotraveler?.first_seen || null;
  const places = args.cotraveler?.distinct_segments
    ?? new Set(scoped.map((e) => e.segment_index).filter((n) => n != null)).size
    ?? 0;
  const sightings = args.cotraveler?.sighting_count || scoped.length;
  const location = locationOf(scoped, args.cotraveler, args.timeline || undefined);
  const score = num(args.cotraveler?.score);
  const prior = args.cotraveler?.distinct_time_windows;
  const cotravel = args.cotraveler
    ? [
      prior != null ? `${prior} prior window${prior === 1 ? '' : 's'}` : null,
      args.cotraveler.survived_reroute ? 'held after reroute' : null,
      score != null ? `${score.toFixed(2)} correlation` : null,
    ].filter(Boolean).join(' · ') || null
    : null;
  const watchlistId = args.cotraveler?.watchlist_id
    ?? num(scoped[0]?.meta?.watchlist_id);
  const ownIdent = str(args.cotraveler?.identifier)
    || str(scoped[0]?.meta?.identifier)
    || str(scoped[0]?.meta?.external_id);

  return {
    id: uniqueId(scoped, args.cotraveler),
    kind,
    title: titleOf(kind, scoped, args.cotraveler),
    identifier: identifierOf(scoped, args.cotraveler),
    trust: trustOf(kind, scoped, args.cotraveler),
    firstObserved: first,
    firstObservedDtg: formatDtg(first),
    sightings,
    places: places || 1,
    cotravel,
    lastDwell: lastDwell(scoped, args.cotraveler, location, units),
    location,
    source: sourceOf(kind, scoped, args.cotraveler),
    confidence: confidenceOf(scoped, args.cotraveler),
    watchlistId,
    canRouteAround: kind !== 'cotraveler' || !!args.cotraveler,
    canMarkOurs: !!ownIdent && kind !== 'drone',
    excludeIdentifier: ownIdent,
    excludeSignalType: args.cotraveler?.signal_type || str(scoped[0]?.meta?.signal_type) || 'wifi',
    excludeName: args.cotraveler?.name || str(scoped[0]?.meta?.name) || null,
    excludeManufacturerId: args.cotraveler?.manufacturer_id
      ?? (typeof scoped[0]?.meta?.manufacturer_id === 'number'
        ? scoped[0].meta.manufacturer_id
        : null),
  };
}
